import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { NavLink, Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  Grid,
  Card,
  Typography,
  Avatar,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  SwapHoriz as TransferIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";
import { format } from "date-fns";

// Import GraphQL operations
import { GET_TICKET } from "./graphql/queries";
import {
  ADD_TICKET_MESSAGE,
  UPDATE_TICKET,
  CLOSE_TICKET,
} from "./graphql/mutations";

// Import components
import TransferTicketDialog from "./components/TransferTicketDialog";
import TicketHistory from "./components/TicketHistory";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const ChatContainer = styled(Grid)`
  width: 100%;
  min-height: 75vh;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ChatMain = styled(Grid)`
  display: flex;
  flex-direction: column;
  height: 75vh;
`;

const ChatMessages = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: ${(props) => props.theme?.spacing?.(3) || "24px"};
  background: ${(props) =>
    props.theme?.palette?.background?.default || "#fafafa"};
`;

const ChatMessage = styled.div`
  margin: ${(props) => props.theme?.spacing?.(2, 0) || "16px 0"};
  display: flex;
  justify-content: ${(props) =>
    props.position === "right" ? "flex-end" : "flex-start"};
`;

const ChatMessageInner = styled.div`
  display: flex;
  align-items: flex-start;
  max-width: 70%;
  gap: ${(props) => props.theme?.spacing?.(1.5) || "12px"};
`;

const ChatMessageBubble = styled.div`
  background: ${(props) =>
    props.highlighted
      ? props.theme?.palette?.primary?.main || "#1976d2"
      : props.theme?.palette?.background?.paper || "#fff"};
  color: ${(props) =>
    props.highlighted
      ? props.theme?.palette?.primary?.contrastText || "#fff"
      : props.theme?.palette?.text?.primary || "#000"};
  border-radius: 18px;
  padding: ${(props) => props.theme?.spacing?.(1.5, 2) || "12px 16px"};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
`;

const ChatInput = styled(Grid)`
  padding: ${(props) => props.theme?.spacing?.(2) || "16px"};
  background: ${(props) => props.theme?.palette?.background?.paper || "#fff"};
  border-top: 1px solid ${(props) => props.theme?.palette?.divider || "#e0e0e0"};
`;

const ChatSidebar = styled(Grid)`
  border-left: 1px solid
    ${(props) => props.theme?.palette?.divider || "#e0e0e0"};
  padding: ${(props) => props.theme?.spacing?.(3) || "24px"};
  background: ${(props) => props.theme?.palette?.background?.paper || "#fff"};
  overflow-y: auto;
  height: 75vh;
`;

const InfoSection = styled(Box)`
  margin-bottom: ${(props) => props.theme?.spacing?.(3) || "24px"};
  padding-bottom: ${(props) => props.theme?.spacing?.(2) || "16px"};
  border-bottom: 1px solid
    ${(props) => props.theme?.palette?.divider || "#e0e0e0"};
`;

// Helper to safely format dates
const safeFormatDate = (dateValue) => {
  if (!dateValue) return "-";
  try {
    // If it's a string that looks like a timestamp (all digits), convert to number
    let value = dateValue;
    if (typeof dateValue === "string" && /^\d+$/.test(dateValue)) {
      value = parseInt(dateValue, 10);
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) return "-";
    return format(date, "MMM dd, yyyy HH:mm");
  } catch (error) {
    console.error("Error formatting date:", dateValue, error);
    return "-";
  }
};

function ChatMessageComponent({ message, currentUserId }) {
  const isCurrentUser =
    message.sender?.id === currentUserId ||
    message.sender?._id === currentUserId;
  const position = isCurrentUser ? "right" : "left";

  return (
    <ChatMessage position={position}>
      <ChatMessageInner>
        {position === "left" && (
          <Avatar
            alt={message.senderName}
            src={message.sender?.avatar}
            sx={{ width: 32, height: 32 }}
          >
            {message.senderName?.charAt(0)}
          </Avatar>
        )}
        <div>
          <ChatMessageBubble highlighted={isCurrentUser}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              {message.senderName}
            </Typography>
            <Typography variant="body2">{message.content}</Typography>
            {message.isInternal && (
              <Chip
                label="Internal"
                size="small"
                color="warning"
                sx={{ mt: 1, height: 20 }}
              />
            )}
          </ChatMessageBubble>
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, opacity: 0.7 }}
          >
            {safeFormatDate(message.timestamp)}
          </Typography>
        </div>
        {position === "right" && (
          <Avatar
            alt={message.senderName}
            src={message.sender?.avatar}
            sx={{ width: 32, height: 32 }}
          >
            {message.senderName?.charAt(0)}
          </Avatar>
        )}
      </ChatMessageInner>
    </ChatMessage>
  );
}

function SupportTicketAdd() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // State
  const [messageText, setMessageText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Get current user ID
  const currentUserId =
    JSON.parse(localStorage.getItem("user"))?.userId ||
    JSON.parse(localStorage.getItem("user"))?.clientId;

  // Query ticket data
  const { data, loading, error, refetch } = useQuery(GET_TICKET, {
    variables: { id },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.ticket) {
        setStatus(data.ticket.status);
        setPriority(data.ticket.priority);
      }
    },
  });

  const ticket = data?.ticket;

  // Mutations
  const [addMessage, { loading: messageLoading }] = useMutation(
    ADD_TICKET_MESSAGE,
    {
      onCompleted: () => {
        setMessageText("");
        setIsInternal(false);
        refetch();
        scrollToBottom();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error sending message: ${error.message}`,
          severity: "error",
        });
      },
    }
  );

  const [updateTicket, { loading: updateLoading }] = useMutation(
    UPDATE_TICKET,
    {
      onCompleted: () => {
        setSnackbar({
          open: true,
          message: "Ticket updated successfully",
          severity: "success",
        });
        refetch();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: `Error updating ticket: ${error.message}`,
          severity: "error",
        });
      },
    }
  );

  const [closeTicket, { loading: closeLoading }] = useMutation(CLOSE_TICKET, {
    onCompleted: () => {
      setSnackbar({
        open: true,
        message: "Ticket closed successfully",
        severity: "success",
      });
      refetch();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error closing ticket: ${error.message}`,
        severity: "error",
      });
    },
  });

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  // Handlers
  const handleSendMessage = () => {
    if (!messageText.trim()) return;

    addMessage({
      variables: {
        input: {
          ticketId: id,
          content: messageText.trim(),
          isInternal,
        },
      },
    });
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus === status) return;

    setStatus(newStatus);
    updateTicket({
      variables: {
        input: {
          ticketId: id,
          status: newStatus,
        },
      },
    });
  };

  const handlePriorityChange = (newPriority) => {
    if (newPriority === priority) return;

    setPriority(newPriority);
    updateTicket({
      variables: {
        input: {
          ticketId: id,
          priority: newPriority,
        },
      },
    });
  };

  const handleCloseTicket = () => {
    if (
      window.confirm(
        "Are you sure you want to close this ticket? This will mark it as resolved."
      )
    ) {
      closeTicket({
        variables: {
          ticketId: id,
        },
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "info";
      case "Pending":
        return "warning";
      case "In Progress":
        return "primary";
      case "Resolved":
        return "success";
      case "Closed":
        return "default";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "default";
      case "Medium":
        return "info";
      case "High":
        return "warning";
      case "Urgent":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error loading ticket: {error.message}</Alert>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box p={3}>
        <Alert severity="warning">Ticket not found</Alert>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={`Ticket ${ticket.ticketNumber}`} />
      <Grid container justifyContent="space-between" spacing={3}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Ticket {ticket.ticketNumber}
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/widget/dashboard">
              Dashboard
            </Link>
            <Link component={NavLink} to="/widget/support-tickets">
              Support Tickets
            </Link>
            <Typography>{ticket.ticketNumber}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="inherit"
            component={Link}
            to="/widget/support-tickets"
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Grid>
      </Grid>
      <Divider my={6} />

      <ChatContainer container component={Card}>
        <ChatMain item xs={12} md={9}>
          {/* Tab Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Conversation" />
              <Tab label="History" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 ? (
            <>
              {/* Messages */}
              <ChatMessages>
                {/* Initial ticket description as first message */}
                <ChatMessage position="left">
                  <ChatMessageInner>
                    <Avatar
                      alt={ticket.createdBy?.name || ticket.customerName}
                      src={ticket.createdBy?.avatar}
                      sx={{ width: 32, height: 32 }}
                    >
                      {(ticket.createdBy?.name || ticket.customerName)?.charAt(
                        0
                      )}
                    </Avatar>
                    <div>
                      <ChatMessageBubble>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: 0.5 }}
                        >
                          {ticket.createdBy?.name ||
                            ticket.customerName ||
                            "Customer"}
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{ mb: 1 }}
                        >
                          {ticket.subject}
                        </Typography>
                        <Typography variant="body2">
                          {ticket.description}
                        </Typography>
                      </ChatMessageBubble>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.5, opacity: 0.7 }}
                      >
                        {safeFormatDate(ticket.createdAt)}
                      </Typography>
                    </div>
                  </ChatMessageInner>
                </ChatMessage>

                {/* Follow-up messages */}
                {ticket.messages?.map((message) => (
                  <ChatMessageComponent
                    key={message.id}
                    message={message}
                    currentUserId={currentUserId}
                  />
                ))}
                <div ref={messagesEndRef} />
              </ChatMessages>

              {/* Input Area */}
              <ChatInput container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Type your message"
                    multiline
                    rows={3}
                    variant="outlined"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={messageLoading || ticket.status === "Closed"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Message Type</InputLabel>
                    <Select
                      value={isInternal ? "internal" : "public"}
                      onChange={(e) =>
                        setIsInternal(e.target.value === "internal")
                      }
                      label="Message Type"
                      disabled={messageLoading || ticket.status === "Closed"}
                    >
                      <MenuItem value="public">
                        Public (visible to customer)
                      </MenuItem>
                      <MenuItem value="internal">
                        Internal (agent only)
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    endIcon={<SendIcon />}
                    size="large"
                    onClick={handleSendMessage}
                    disabled={
                      messageLoading ||
                      !messageText.trim() ||
                      ticket.status === "Closed"
                    }
                  >
                    {messageLoading ? "Sending..." : "Send"}
                  </Button>
                </Grid>
              </ChatInput>
            </>
          ) : (
            <Box sx={{ p: 3, overflow: "auto" }}>
              <TicketHistory history={ticket.history} />
            </Box>
          )}
        </ChatMain>

        {/* Sidebar */}
        <ChatSidebar item xs={12} md={3}>
          {/* Ticket Info */}
          <InfoSection>
            <Typography variant="h6" gutterBottom>
              Ticket Details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={ticket.status}
                  color={getStatusColor(ticket.status)}
                  size="small"
                />
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Priority
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={ticket.priority}
                  color={getPriorityColor(ticket.priority)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {safeFormatDate(ticket.createdAt)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {safeFormatDate(ticket.updatedAt)}
              </Typography>
            </Box>
          </InfoSection>

          {/* Customer Info */}
          {(ticket.customerName || ticket.customerEmail) && (
            <InfoSection>
              <Typography variant="h6" gutterBottom>
                Customer
              </Typography>
              {ticket.customerName && (
                <Typography variant="body2" gutterBottom>
                  {ticket.customerName}
                </Typography>
              )}
              {ticket.customerEmail && (
                <Typography variant="body2" color="text.secondary">
                  {ticket.customerEmail}
                </Typography>
              )}
            </InfoSection>
          )}

          {/* Assignee Info */}
          <InfoSection>
            <Typography variant="h6" gutterBottom>
              Assigned To
            </Typography>
            {ticket.assignedTo ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  src={ticket.assignedTo.avatar}
                  alt={ticket.assignedTo.name}
                  sx={{ width: 32, height: 32 }}
                >
                  {ticket.assignedTo.name?.charAt(0)}
                </Avatar>
                <Typography variant="body2">
                  {ticket.assignedTo.name}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Unassigned
              </Typography>
            )}
          </InfoSection>

          {/* Quick Actions */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => handleStatusChange(e.target.value)}
                label="Status"
                disabled={updateLoading || ticket.status === "Closed"}
              >
                <MenuItem value="Open">Open</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Resolved">Resolved</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                onChange={(e) => handlePriorityChange(e.target.value)}
                label="Priority"
                disabled={updateLoading || ticket.status === "Closed"}
              >
                <MenuItem value="Low">Low</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<TransferIcon />}
              onClick={() => setOpenTransferDialog(true)}
              sx={{ mb: 1 }}
              disabled={ticket.status === "Closed"}
            >
              Transfer Ticket
            </Button>

            {ticket.status !== "Closed" && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={handleCloseTicket}
                disabled={closeLoading}
              >
                {closeLoading ? "Closing..." : "Close Ticket"}
              </Button>
            )}
          </Box>
        </ChatSidebar>
      </ChatContainer>

      {/* Transfer Dialog */}
      <TransferTicketDialog
        open={openTransferDialog}
        onClose={() => setOpenTransferDialog(false)}
        onSuccess={() => {
          setOpenTransferDialog(false);
          setSnackbar({
            open: true,
            message: "Ticket transferred successfully",
            severity: "success",
          });
          refetch();
        }}
        ticket={ticket}
        spaceId={ticket.spaceId}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default SupportTicketAdd;
